import React, { useState, useEffect, useRef, useCallback } from 'react';
import './PodLogsDrawer.css';

// Errors that indicate the container isn't currently running — suggest --previous
const CONTAINER_NOT_RUNNING_MSGS = ['container not found', 'is not running', 'ContainerCreating', 'CrashLoopBackOff', 'waiting to start'];

/**
 * Live pod logs.
 *
 * This connects DIRECTLY to the API's /logs/stream WebSocket, which runs `kubectl logs -f`
 * and writes lines straight down the socket.
 *
 * It replaces a Redis pub/sub design that was the cause of "logs sometimes show nothing":
 * pub/sub has no retention, so any line published before the browser had SUBSCRIBEd was
 * lost forever. That race was worked around by guessing the channel name, opening a socket
 * early, stashing it on `window._tempLogWs`, waiting up to 2s, and having the server
 * sleep 1.2s before publishing. None of that is needed now — the socket is already open
 * before kubectl starts, so there is nothing to miss, and no artificial delay.
 */
export default function PodLogsDrawer({ clusterId, namespace, podName, onClose }) {
  const [lines, setLines]         = useState([]);
  const [status, setStatus]       = useState('connecting'); // connecting | streaming | done | error
  const [error, setError]         = useState('');
  const [tail, setTail]           = useState(100);
  const [autoScroll, setAutoScroll] = useState(true);
  const [containerNotRunning, setContainerNotRunning] = useState(false);
  const bottomRef                 = useRef(null);
  const containerRef              = useRef(null);
  const wsRef                     = useRef(null);

  const closeSocket = useCallback(() => {
    const ws = wsRef.current;
    wsRef.current = null;
    if (!ws) return;
    // Drop the handlers first so a close we initiated isn't reported as a stream error.
    ws.onmessage = null; ws.onerror = null; ws.onclose = null;
    try { ws.close(); } catch (e) { /* already closing */ }
  }, []);

  const startStream = useCallback((tailLines, usePrevious = false) => {
    closeSocket();
    setLines([]);
    setStatus('connecting');
    setError('');
    setContainerNotRunning(false);

    // The token goes in the query string because a browser cannot set headers on a
    // WebSocket. The server validates it and applies the same cluster RBAC as the HTTP API.
    const token = localStorage.getItem('token') || '';
    const base = process.env.REACT_APP_API_URL || '';
    const wsBase = base.replace(/^http/, 'ws');
    const qs = new URLSearchParams({ token, tail: String(tailLines) });
    if (usePrevious) qs.set('previous', 'true');
    const url =
      `${wsBase}/clusters/${clusterId}/pods/${encodeURIComponent(namespace)}` +
      `/${encodeURIComponent(podName)}/logs/stream?${qs.toString()}`;

    let ws;
    try {
      ws = new WebSocket(url);
    } catch (e) {
      setStatus('error');
      setError(e.message || 'Could not open log stream');
      return;
    }
    wsRef.current = ws;

    ws.onmessage = (event) => {
      let msg;
      try { msg = JSON.parse(event.data); } catch (e) { return; }
      if (msg.type === 'log_start') {
        setStatus('streaming');
      } else if (msg.type === 'log_line') {
        setLines(prev => [...prev, msg.line]);
      } else if (msg.type === 'log_end') {
        setStatus('done');
      } else if (msg.type === 'log_error') {
        setStatus('error');
        const errMsg = msg.message || 'Unknown error';
        setError(errMsg);
        // Offer --previous only when the container isn't running AND we aren't already
        // showing the previous container's logs.
        if (!usePrevious && CONTAINER_NOT_RUNNING_MSGS.some(m => errMsg.toLowerCase().includes(m.toLowerCase()))) {
          setContainerNotRunning(true);
        }
      }
    };

    ws.onerror = () => {
      // Only surface this if nothing has streamed yet; a mid-stream blip that the server
      // closes cleanly is handled by onclose below.
      setStatus(prev => (prev === 'connecting' ? 'error' : prev));
      setError(prev => prev || 'Log stream connection failed');
    };

    ws.onclose = (ev) => {
      if (wsRef.current !== ws) return;      // we closed it deliberately
      wsRef.current = null;
      // 1008 is the policy-violation code the server uses for auth/RBAC refusals.
      if (ev.code === 1008) {
        setStatus('error');
        setError(ev.reason || 'Not authorized to view logs for this cluster');
      } else {
        setStatus(prev => (prev === 'streaming' ? 'done' : prev));
      }
    };
  }, [clusterId, namespace, podName, closeSocket]);

  // Open on mount, and re-open when the tail size changes (kubectl needs restarting to
  // change --tail). Always tear the socket down on unmount, or `kubectl logs -f` keeps
  // running server-side for a drawer nobody is looking at.
  useEffect(() => {
    startStream(tail);
    return closeSocket;
  }, [startStream, tail, closeSocket]);

  // Auto-scroll. Instant ('auto'), not 'smooth' — on a fast/bursty stream (thousands of
  // lines arriving in quick succession) a smooth-scroll animation can't keep up: each new
  // batch of lines restarts/extends the animation, so the scroll position is still
  // mid-transition (genuinely not at the bottom yet) when the scroll event it generates
  // reaches handleScroll below. That looked identical to a manual scroll-away and silently
  // flipped autoScroll off mid-stream — confirmed live on a high-volume pod (reported:
  // "every time we have to click Scroll to bottom", i.e. auto-scroll kept disabling itself).
  useEffect(() => {
    if (autoScroll && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'auto' });
    }
  }, [lines, autoScroll]);

  // Detect manual scroll up to disable auto-scroll. Threshold widened (40px -> 120px):
  // even with instant scroll, a fast burst can render several lines between one scroll
  // event and the next, landing slightly short of the true bottom on that tick alone —
  // 120px comfortably covers a burst without being so loose it ignores a genuine
  // scroll-up of a few lines.
  const handleScroll = () => {
    const el = containerRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    setAutoScroll(atBottom);
  };

  const handleDownload = () => {
    const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `${podName}-logs.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // startStream closes any existing socket itself, so Reload is just a re-open.
  const handleReload = (usePrevious = false) => startStream(tail, usePrevious);

  return (
    <div className="log-drawer-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="log-drawer">
        {/* Header */}
        <div className="log-drawer-header">
          <div className="log-drawer-title">
            <span className="log-drawer-icon">📋</span>
            <div>
              <div className="log-drawer-pod">{podName}</div>
              <div className="log-drawer-ns">{namespace}</div>
            </div>
          </div>
          <div className="log-drawer-controls">
            <select
              value={tail}
              onChange={e => setTail(Number(e.target.value))}
              className="log-tail-select"
            >
              <option value={50}>Last 50</option>
              <option value={100}>Last 100</option>
              <option value={200}>Last 200</option>
              <option value={500}>Last 500</option>
            </select>
            <button className="log-btn" onClick={handleReload} title="Reload logs">↺ Reload</button>
            <button className="log-btn" onClick={handleDownload} title="Download logs" disabled={lines.length === 0}>⬇ Download</button>
            <button className="log-btn log-btn-close" onClick={onClose}>✕</button>
          </div>
        </div>

        {/* Status bar */}
        <div className={`log-status-bar ${status}`}>
          {status === 'connecting' && <span>⟳ Connecting to log stream...</span>}
          {status === 'streaming' && <span>● Live — {lines.length} lines</span>}
          {status === 'done'      && <span>✓ Stream ended — {lines.length} lines</span>}
          {status === 'error'     && (
          <span>
            ✗ Error: {error}
            {containerNotRunning && (
              <button
                onClick={() => startStream(tail, true)}
                style={{ marginLeft: 10, padding: '2px 8px', fontSize: 11, background: 'var(--ink-2)',
                  color: 'var(--color-warning-500)', border: '1px solid var(--color-warning-500)', borderRadius: 4, cursor: 'pointer' }}>
                ⚡ Try --previous (show last run's logs)
              </button>
            )}
          </span>
        )}
          {!autoScroll && status === 'streaming' && (
            <button className="log-scroll-btn" onClick={() => { setAutoScroll(true); bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }}>
              ↓ Scroll to bottom
            </button>
          )}
        </div>

        {/* Log content */}
        <div className="log-content" ref={containerRef} onScroll={handleScroll}>
          {lines.length === 0 && status === 'connecting' && (
            <div className="log-empty">Waiting for logs...</div>
          )}
          {lines.length === 0 && status === 'done' && (
            <div className="log-empty">No logs available for this pod.</div>
          )}
          {lines.map((line, i) => (
            <div key={i} className="log-line">
              <span className="log-line-num">{i + 1}</span>
              <span className="log-line-text">{line}</span>
            </div>
          ))}
          <div ref={bottomRef} />
        </div>
      </div>
    </div>
  );
}
